import React, { useEffect, useMemo, useState } from 'react';
import { Layout, theme, Button, Menu, Spin, Typography, Avatar, Tooltip } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../features/auth/hooks/useAuth';
import { usePermissions } from '../features/permissions/PermissionsContext';
import { NAV_GROUPS } from '../features/permissions/navConfig';
import { appPath } from '../routes/paths';
import { SETUP_CHILD_ROUTES, setupPath } from '../features/settings/setupRoutes';
import {
  ApartmentOutlined,
  CloudServerOutlined,
  ContactsOutlined,
  FundOutlined,
  PartitionOutlined,
  RocketOutlined,
  FileTextOutlined,
  TeamOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  SearchOutlined,
  ScheduleOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  UsergroupAddOutlined,
  LogoutOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import CommandSearch from './CommandSearch';
import { usePageTitle } from '../hooks/usePageTitle';
import { uxTokens } from '../design/ux';
import { PageShellProvider } from '../shell/PageShellProvider';
import PageChrome from '../shell/PageChrome';
import SidebarBrand from './SidebarBrand';
import ThemeToggle from './ThemeToggle';
import LicenseWarningBanner from './LicenseWarningBanner';
import ImpersonationBanner from './ImpersonationBanner';
import { useAuthStore } from '../features/auth/store/authStore';

const { Sider, Content } = Layout;
const { Text } = Typography;

const ICONS: Record<string, React.ReactNode> = {
  [appPath()]: <BarChartOutlined />,
  [appPath('/task-dashboard')]: <ThunderboltOutlined />,
  [appPath('/contacts')]: <ContactsOutlined />,
  [appPath('/leads')]: <FundOutlined />,
  [appPath('/leads/pipeline')]: <PartitionOutlined />,
  [appPath('/documents')]: <FileTextOutlined />,
  [appPath('/offerings')]: <ShoppingCartOutlined />,
  [appPath('/offerings/categories')]: <TagsOutlined />,
  [appPath('/campaigns')]: <RocketOutlined />,
  [appPath('/tasks')]: <ScheduleOutlined />,
  [appPath('/commissions/calculator')]: <DollarOutlined />,
  [appPath('/commissions/reports')]: <BarChartOutlined />,
  [appPath('/admin/users')]: <TeamOutlined />,
  [appPath('/admin/roles')]: <SafetyCertificateOutlined />,
  [appPath('/bulk-operations')]: <DatabaseOutlined />,
  [appPath('/host')]: <CloudServerOutlined />,
  ...Object.fromEntries(
    SETUP_CHILD_ROUTES.map((route) => {
      const icon =
        route.path === 'lead-pipeline-stages'
          ? <ApartmentOutlined />
          : route.path === 'customer-segments'
            ? <UsergroupAddOutlined />
            : <ContactsOutlined />;
      return [setupPath(route.path), icon];
    }),
  ),
};

const AppLayout: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, fetchCurrentUser, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const isImpersonating = useAuthStore((state) => Boolean(state.user?.isImpersonating));

  usePageTitle();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user === null) {
      void fetchCurrentUser();
    }
  }, [isAuthenticated, user, fetchCurrentUser]);

  const handleLogout = () => {
    void logout();
    navigate('/login', { replace: true });
  };

  const userEmail = user?.email ?? '';

  const userInitials = useMemo(() => {
    if (user?.firstName || user?.lastName) {
      const parts = [user.firstName, user.lastName].filter(Boolean) as string[];
      if (parts.length >= 2) {
        return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
      }
      return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
    }
    const local = userEmail.split('@')[0] ?? '';
    return (local.slice(0, 2) || '?').toUpperCase();
  }, [user, userEmail]);

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const sidebarItems: MenuProps['items'] = useMemo(() => {
    return NAV_GROUPS.map((group) => ({
      type: 'group' as const,
      label: group.label,
      children: group.children
        .filter((item) => !item.permission || hasPermission(item.permission))
        .map((item) => ({
          key: item.key,
          icon: ICONS[item.key],
          label: item.label,
        })),
    })).filter((group) => group.children && group.children.length > 0);
  }, [hasPermission]);

  return (
    <Layout
      className={isImpersonating ? 'cp-layout cp-layout--impersonation' : 'cp-layout'}
      style={{ height: '100vh', overflow: 'hidden', flexDirection: 'column' }}
    >
      <CommandSearch />
      {isImpersonating ? <ImpersonationBanner /> : null}
      <Layout className="cp-layout__body" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Sider
        width={uxTokens.layout.sidebarWidth}
        className="cp-layout__sider"
        breakpoint="lg"
        collapsedWidth={56}
        collapsible
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
        trigger={null}
        style={{
          borderRight: `1px solid ${token.colorBorderSecondary}`,
          height: '100%',
        }}
      >
        <div className="cp-sidebar">
          <div className="cp-sidebar-top">
            <div className="cp-sidebar-brand-slot">
              <SidebarBrand collapsed={sidebarCollapsed} />
            </div>
            <div className="cp-sidebar-top-tools">
              <Button
                block
                icon={<SearchOutlined />}
                onClick={() => window.dispatchEvent(new Event('open-command-search'))}
                className="cp-sidebar-search"
              >
                Search <span className="cp-sidebar-search-kbd">Ctrl+K</span>
              </Button>
              <Button
                type="text"
                size="small"
                className="cp-sidebar-collapse-btn"
                icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setSidebarCollapsed(prev => !prev)}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              />
            </div>
          </div>

          <div className="cp-sidebar-nav">
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={sidebarItems}
              onClick={handleMenuClick}
              style={{ borderRight: 0 }}
              className="cp-nav-menu"
            />
          </div>

          <div
            className="cp-sidebar-footer"
            style={{ borderTop: `1px solid ${token.colorBorderSecondary}` }}
          >
            {user ? (
              <div className="cp-sidebar-user-strip">
                <button
                  type="button"
                  className="cp-sidebar-user-identity"
                  onClick={() => navigate(appPath('/profile'))}
                >
                  <Avatar size={32} className="cp-sidebar-user-avatar">
                    {userInitials}
                  </Avatar>
                  <Text ellipsis className="cp-sidebar-user-name">
                    {userEmail}
                  </Text>
                </button>
                <div className="cp-sidebar-user-actions">
                  <ThemeToggle />
                  <Tooltip title="Sign out">
                    <Button
                      type="text"
                      size="small"
                      icon={<LogoutOutlined />}
                      onClick={handleLogout}
                      aria-label="Sign out"
                      className="cp-sidebar-user-logout"
                    />
                  </Tooltip>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
                <Spin size="small" />
              </div>
            )}
            <div style={{ padding: '4px 16px 8px', textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>v1.0.4</Text>
            </div>
          </div>
        </div>
      </Sider>
      <Content
        className="cp-layout__content"
        style={{
          padding: uxTokens.spacing.lg,
          margin: 0,
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <PageShellProvider>
          <div className="cp-app-shell">
            <PageChrome />
            <LicenseWarningBanner />
            <main className="cp-app-main">
              <Outlet />
            </main>
          </div>
        </PageShellProvider>
      </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
